<template>
  <div class="enrollmentDetail">
    <div v-if="loading" class="detailLoading">Loading timeline…</div>
    <div v-else-if="error" class="detailError">{{ error }}</div>
    <div v-else-if="outboundItems.length === 0" class="detailEmpty">No reachouts recorded for this enrollment.</div>
    <div v-else class="timeline">
      <div
        v-for="item in outboundItems"
        :key="item.id"
        class="timelineStep"
        :class="{ clickable: true }"
        role="button"
        tabindex="0"
        :title="'Click to open reachout'"
        @click="emit('reachout-click', item.id)"
        @keydown.enter.prevent="emit('reachout-click', item.id)"
      >
        <div class="stepIconCol">
          <div class="stepIcon">
            <AppIcon v-if="item.reachout_type === 'reachout_email'" name="email" :size="14" />
            <AppIcon v-else-if="item.reachout_type === 'voicemail'" name="voicemail" :size="14" />
            <img
              v-else-if="item.reachout_type === 'linkedin_connect'"
              :src="linkedinFavicon"
              width="14"
              height="14"
              alt="LinkedIn"
              style="object-fit:contain;border-radius:2px;"
            />
            <AppIcon v-else name="email" :size="14" />
          </div>
          <div class="stepLine" />
        </div>

        <div class="stepContent">
          <div class="stepHeader">
            <span class="stepLabel">{{ stepLabel(item) }}</span>
            <span class="stepDate">{{ fmtDate(item.created_at) }}</span>
            <span class="stepStatus" :class="`stepStatus-${item.status}`">{{ item.status }}</span>
          </div>

          <div v-if="item.subject" class="stepSubject">{{ item.subject }}</div>
          <div v-if="item.body_snippet" class="stepBody">{{ item.body_snippet }}</div>

          <!-- Nested replies/bounces for this outbound item -->
          <div
            v-for="reply in (replyMap[String(item.id)] || [])"
            :key="reply.id"
            class="replyBlock"
            :class="{ 'replyBlock-bounce': reply.is_bounce }"
            @click.stop
          >
            <div class="replyLabel">
              <AppIcon :name="reply.is_bounce ? 'close' : 'task'" :size="12" />
              {{ reply.is_bounce ? 'Bounced' : 'Reply received' }}
              <span v-if="reply.reply_snippet" class="replyFrom">· {{ reply.reply_snippet }}</span>
            </div>
            <div v-if="reply.body_snippet" class="replySnippet">{{ reply.body_snippet }}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import AppIcon from './AppIcon.vue'
import { apiFetch } from '../services/api.ts'
import { googleFaviconUrl } from '../services/favicons.ts'

const props = defineProps({
  enrollmentId: { type: String, required: true },
  sequenceSteps: { type: Array, default: () => [] },
})

const emit = defineEmits(['reachout-click'])

const loading = ref(false)
const error = ref('')
const items = ref([])

const linkedinFavicon = googleFaviconUrl('linkedin.com', { size: 32 })

// Only outbound items are rendered as top-level timeline entries
const outboundItems = computed(() => items.value.filter(i => i.item_kind === 'outbound'))

// Map parent reachout ID → list of nested reply/bounce items
const replyMap = computed(() => {
  const map = {}
  for (const item of items.value) {
    if (item.replied_to_reachout_id) {
      const key = String(item.replied_to_reachout_id)
      if (!map[key]) map[key] = []
      map[key].push(item)
    }
  }
  return map
})

async function load() {
  if (!props.enrollmentId) return
  loading.value = true
  error.value = ''
  try {
    const resp = await apiFetch(`/sequence-enrollments/${encodeURIComponent(props.enrollmentId)}/reachouts`)
    items.value = Array.isArray(resp) ? resp : []
  } catch (e) {
    error.value = e?.message || 'Failed to load timeline'
  } finally {
    loading.value = false
  }
}

function stepLabel(item) {
  const stepNum = item.step_index != null ? `Step ${item.step_index + 1}` : null
  const typeLabels = {
    reachout_email: 'Email',
    linkedin_connect: 'LinkedIn',
    voicemail: 'Voicemail',
  }
  const typeLabel = typeLabels[item.reachout_type] || item.reachout_type
  return stepNum ? `${stepNum} · ${typeLabel}` : typeLabel
}

function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

onMounted(load)
watch(() => props.enrollmentId, load)
</script>

<style scoped>
.enrollmentDetail {
  padding: 12px 0 4px;
}

.detailLoading,
.detailEmpty {
  font-size: 13px;
  color: var(--muted);
  padding: 8px 0;
}

.detailError {
  font-size: 13px;
  color: var(--danger);
  padding: 8px 0;
}

.timeline {
  display: flex;
  flex-direction: column;
}

.timelineStep {
  display: flex;
  gap: 12px;
  position: relative;
  border-radius: 8px;
  padding: 4px 6px 4px 0;
  margin: 0 -6px;
  cursor: pointer;
  transition: background 0.1s;
}

.timelineStep:hover {
  background: var(--row-hover-bg);
}

.timelineStep:focus-visible {
  outline: 2px solid var(--focus-border);
  outline-offset: 2px;
}

.stepIconCol {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 28px;
}

.stepIcon {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  display: grid;
  place-items: center;
  flex-shrink: 0;
  /* All outbound icons are grey; color is conveyed by status badge */
  color: var(--muted);
}

.stepLine {
  flex: 1;
  width: 1px;
  background: var(--border2);
  margin: 4px 0;
  min-height: 12px;
}

.timelineStep:last-child .stepLine {
  display: none;
}

.stepContent {
  flex: 1;
  min-width: 0;
  padding-bottom: 16px;
}

.stepHeader {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}

.stepLabel {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
}

.stepDate {
  font-size: 12px;
  color: var(--muted);
}

.stepStatus {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 5px;
  border-radius: 4px;
  background: var(--surface2, var(--surface));
  color: var(--muted);
  border: 1px solid var(--border);
}

/* Blue for outbound sent (not green — green is reserved for replies) */
.stepStatus-sent { color: var(--primary); border-color: var(--primary); background: color-mix(in srgb, var(--primary) 8%, transparent); }
.stepStatus-pending_approval { color: var(--warning, #f59e0b); border-color: var(--warning, #f59e0b); background: color-mix(in srgb, var(--warning, #f59e0b) 8%, transparent); }
.stepStatus-failed { color: var(--danger); border-color: var(--danger); background: color-mix(in srgb, var(--danger) 8%, transparent); }
.stepStatus-replied { color: var(--success); border-color: var(--success); background: color-mix(in srgb, var(--success) 8%, transparent); }

.stepSubject {
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stepBody {
  font-size: 12px;
  color: var(--muted);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Nested reply / bounce block */
.replyBlock {
  margin-top: 8px;
  padding: 8px 10px;
  border-left: 2px solid var(--success);
  background: color-mix(in srgb, var(--success) 6%, var(--surface));
  border-radius: 0 6px 6px 0;
  cursor: default;
}

.replyBlock-bounce {
  border-left-color: var(--danger);
  background: color-mix(in srgb, var(--danger) 6%, var(--surface));
}

.replyLabel {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--success);
  margin-bottom: 4px;
}

.replyBlock-bounce .replyLabel {
  color: var(--danger);
}

.replyFrom {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
  color: var(--muted);
}

.replySnippet {
  font-size: 12px;
  color: var(--text);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
